import type { AgentMeta } from '../../features/campaigns/columns/agentTypeMetadata'
import { clientInstance } from '../../utils/http-client'
import { baseAPICall } from '../baseAPICall'
import type {
  AgentPreflight,
  CampaignColumn,
  CampaignColumnValue,
  DefineColumnRequest,
  EditValueRequest,
  RecomputeRequest,
  TestPromptRequest,
  TestPromptResponse,
  UpdateColumnRequest,
} from './types'

export async function listCampaignColumns(campaignId: number) {
  return baseAPICall<{ results: CampaignColumn[] }>(() =>
    clientInstance.get('campaign-columns/', {
      params: { campaign: campaignId },
    })
  )
}

export async function listCampaignColumnValues(columnId: number) {
  return baseAPICall<{ results: CampaignColumnValue[] }>(() =>
    clientInstance.get(`campaign-columns/${columnId}/values/`)
  )
}

export async function createCampaignColumn(
  campaignId: number,
  payload: DefineColumnRequest
) {
  return baseAPICall<CampaignColumn>(() =>
    clientInstance.post('campaign-columns/', payload, {
      params: { campaign: campaignId },
    })
  )
}

export async function updateCampaignColumn(
  columnId: number,
  payload: UpdateColumnRequest
) {
  return baseAPICall<CampaignColumn>(() =>
    clientInstance.patch(`campaign-columns/${columnId}/`, payload)
  )
}

export async function deleteCampaignColumn(columnId: number) {
  return baseAPICall<void>(() =>
    clientInstance.delete(`campaign-columns/${columnId}/`)
  )
}

export async function recomputeCampaignColumn(
  columnId: number,
  payload: RecomputeRequest
) {
  return baseAPICall<CampaignColumn>(() =>
    clientInstance.post(`campaign-columns/${columnId}/recompute/`, payload)
  )
}

export async function editCampaignColumnValue(
  columnId: number,
  contactId: number,
  payload: EditValueRequest
) {
  return baseAPICall<CampaignColumnValue>(() =>
    clientInstance.patch(
      `campaign-columns/${columnId}/values/${contactId}/`,
      payload
    )
  )
}

export async function testCampaignColumnPrompt(payload: TestPromptRequest) {
  return baseAPICall<TestPromptResponse>(() =>
    clientInstance.post('campaign-columns/test/', payload)
  )
}

export async function listAgentTypes(campaignId?: number) {
  return baseAPICall<{ results: AgentMeta[] }>(() =>
    clientInstance.get('campaign-columns/agent-types/', {
      params: campaignId ? { campaign: campaignId } : undefined,
    })
  )
}

export async function getAgentPreflight(
  campaignId: number,
  agentType: string,
  productIds: number[] = []
) {
  const params: Record<string, unknown> = {
    campaign: campaignId,
    agent_type: agentType,
  }
  if (productIds.length > 0) {
    params.product_ids = productIds
  }
  return baseAPICall<AgentPreflight>(() =>
    clientInstance.get('campaign-columns/agent-preflight/', { params })
  )
}

export async function approveCampaignColumn(columnId: number) {
  return baseAPICall<CampaignColumn>(() =>
    clientInstance.post(`campaign-columns/${columnId}/approve/`)
  )
}
